import { useSignal } from "~/sdk/signal";
import { useCallback } from "react";
import Button from "~/components/ui/Button";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { useCart } from "~/hooks/useCart";
import type { SimulationOrderForm, SKU, Sla } from "@decocms/apps-vtex/utils/types";

export interface Props {
  items: Array<SKU>;
  additionalSimulationText?: string;
  /**
   * Chamado com o método de frete mais barato após cada simulação (ou
   * `null` se o CEP não teve opções de entrega), pra quem monta o gatilho
   * do acordeão (`common/Cart.tsx`) poder mostrar um resumo do valor ali
   * mesmo, sem precisar manter o painel aberto. Vai junto o CEP simulado --
   * é o mesmo dado usado pra decidir a mensagem de frete grátis, então
   * precisa ser exatamente o CEP que gerou esse preço, nunca um estado
   * derivado separado (evita a mensagem e o cálculo de frete divergirem).
   */
  onResult?: (cheapest: { price: number; postalCode: string } | null) => void;
}

const formatShippingEstimate = (estimate: string) => {
  const [, time, type] = estimate.split(/(\d+)/);

  if (type === "bd") return `${time} dias úteis`;
  if (type === "d") return `${time} dias`;
  if (type === "h") return `${time} horas`;
};

function ShippingContent({
  simulation,
  additionalSimulationText,
}: {
  simulation: { value: SimulationOrderForm | null };
  additionalSimulationText?: string;
}) {
  const { cart } = useCart();

  const methods = simulation.value?.logisticsInfo?.reduce(
    (initial, { slas }) => [...initial, ...slas],
    [] as Sla[],
  ) ?? [];

  const locale = cart.value?.clientPreferencesData?.locale || "pt-BR";
  const currencyCode = cart.value?.storePreferencesData?.currencyCode || "BRL";

  if (simulation.value == null) {
    return null;
  }

  if (methods.length === 0) {
    // Slas vazias não significam necessariamente CEP inválido: a VTEX usa o
    // mesmo formato (logisticsInfo sem slas) tanto pra "esse CEP não recebe
    // entrega" (code: cannotBeDelivered) quanto pra "item sem estoque"
    // (code: withoutStock) -- este último não tem nada a ver com o CEP
    // digitado, e mostrar "CEP inválido" nesse caso faz o cliente duvidar
    // (com razão) de um CEP que na verdade é válido.
    const outOfStock = simulation.value?.messages?.some(
      (message) => message.code === "withoutStock",
    );

    return (
      <div className="p-2">
        <span className="text-sm">
          {outOfStock
            ? "Um item do carrinho está sem estoque no momento"
            : "CEP inválido"}
        </span>
      </div>
    );
  }

  const cheapestMethod = methods.reduce((prev, curr) =>
    curr.price < prev.price ? curr : prev
  );

  const fastestMethod = methods.reduce((prev, curr) =>
    parseInt(curr.shippingEstimate) < parseInt(prev.shippingEstimate)
      ? curr
      : prev
  );

  const filteredMethods = [
    cheapestMethod,
    fastestMethod.id !== cheapestMethod.id ? fastestMethod : null,
  ].filter((method): method is Sla => method !== null);

  return (
    <ul className="flex flex-col gap-1 text-sm mt-1">
      {filteredMethods.map((method) => (
        <li key={method.id} className="flex items-center gap-2 py-1">
          <span className="font-medium">
            {method.price === 0
              ? "Grátis"
              : formatPrice(method.price / 100, currencyCode, locale)}
          </span>
          <span className="text-base-300">
            Frete {method.name}, entrega em{" "}
            {formatShippingEstimate(method.shippingEstimate)} para o CEP{" "}
            {simulation.value?.postalCode}
          </span>
        </li>
      ))}
      {additionalSimulationText && (
        <span className="text-base-300">{additionalSimulationText}</span>
      )}
    </ul>
  );
}

function ShippingSimulation({ items, additionalSimulationText, onResult }: Props) {
  const postalCode = useSignal("");
  const loading = useSignal(false);
  const simulateResult = useSignal<SimulationOrderForm | null>(null);
  const { simulate, sendAttachment, cart } = useCart();

  const handleSimulation = useCallback(async () => {
    if (postalCode.value.length < 9) {
      return;
    }

    const country = cart.value?.storePreferencesData?.countryCode || "BRA";

    try {
      loading.value = true;
      const result = await simulate({
        // Sempre seller 1: com cookie de região, a PDP/minicart podem trazer
        // o seller de uma loja (ex.: dtbarigui) como melhor oferta, e a VTEX
        // calcula o frete a partir do seller informado -- o valor do CEP
        // mudava conforme o seller (Econômica 80035-090: R$ 7,61 no seller 1,
        // R$ 9,53 em dtbarigui, R$ 18,17 em dtpatiobatel).
        items: items.map((sku) => ({ ...sku, id: String(sku.id), seller: "1" })),
        postalCode: postalCode.value,
        country,
      }) as SimulationOrderForm | null;
      simulateResult.value = result;

      const methods = result?.logisticsInfo?.reduce(
        (initial, { slas }) => [...initial, ...slas],
        [] as Sla[],
      ) ?? [];
      const cheapest = methods.length > 0
        ? methods.reduce((prev, curr) => (curr.price < prev.price ? curr : prev))
        : null;
      onResult?.(
        cheapest ? { price: cheapest.price, postalCode: postalCode.value } : null,
      );

      const hasShippingMethod = methods.length > 0;

      // simulate() só consulta um endpoint stateless de prévia — não grava o
      // CEP no orderForm real. Sem isso, o cliente digitaria o CEP de novo
      // na etapa de entrega do checkout da VTEX. Roda em paralelo (não
      // bloqueia a exibição do resultado da simulação) e falha em silêncio:
      // sendAttachment já engole o erro internamente, e o pior caso é só o
      // checkout pedir o CEP de novo, igual ao comportamento de hoje.
      if (hasShippingMethod && cart.value?.orderFormId) {
        sendAttachment({
          attachment: "shippingData",
          body: {
            selectedAddresses: [{ postalCode: postalCode.value, country }],
          },
        });
      }
    } finally {
      loading.value = false;
    }
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full">
      <span className="text-xs uppercase font-semibold tracking-wide text-base-300">
        Calcule o frete e o prazo
      </span>

      <form
        className="join w-full max-w-[400px]"
        onSubmit={(e) => {
          e.preventDefault();
          handleSimulation();
        }}
      >
        <input
          type="text"
          className="input input-sm join-item grow"
          placeholder="Digite seu CEP aqui"
          value={postalCode.value}
          maxLength={9}
          size={9}
          onChange={(e) => {
            const { value } = e.currentTarget;
            const formattedValue = value.replace(/^(\d{5})(\d{3})$/, "$1-$2");
            postalCode.value = formattedValue;
          }}
        />
        <Button
          type="submit"
          aria-label="Calcular"
          loading={loading.value}
          className="btn btn-neutral btn-sm join-item"
        >
          Calcular
        </Button>
      </form>

      <a
        title="Não sei meu CEP"
        className="text-xs underline text-base-300 self-start"
        target="_blank"
        href="http://www.buscacep.correios.com.br/sistemas/buscacep/"
      >
        Não sei meu CEP
      </a>

      <ShippingContent
        simulation={simulateResult}
        additionalSimulationText={additionalSimulationText}
      />
    </div>
  );
}

export default ShippingSimulation;
